import { useState } from "react";
import { AdminShell } from "@project/admin/components/admin-shell";
import { ApiResourceTable } from "@project/admin/components/api-resource-table";
import { DonationRequestDetailDialog } from "@project/admin/components/donation-request-detail-dialog";
import { DonationRequestStatusActions } from "@project/admin/components/donation-request-status-actions";
import { DonationPickupUpdateAction } from "@project/admin/components/donation-pickup-update-action";
import type { Field } from "@project/admin/components/resource-table";
import { adminScrapDonationService } from "@project/services/admin/scrap-donation.service";
import { itemCategoryService } from "@project/services/admin/item-category.service";
import {
  DONATION_REQUEST_FILTER_STATUS_OPTIONS,
  DONATION_REQUEST_STATUS_OPTIONS,
  type DonationRequestStatus,
  type ScrapDonationPayload,
  type ScrapDonationRecord,
} from "@project/types/item-donation";

const statusOptions = DONATION_REQUEST_STATUS_OPTIONS.map(o => ({
  value: o.value,
  label: o.label,
}));

const statusFilterOptions = DONATION_REQUEST_FILTER_STATUS_OPTIONS.map(o => ({
  value: o.value,
  label: o.label,
}));

const fields: Field[] = [
  {
    name: "full_name",
    label: "Full Name",
    required: true,
    showInTable: true,
  },
  {
    name: "mobile_number",
    label: "Mobile",
    type: "tel",
    required: true,
    showInTable: true,
  },
  { name: "email", label: "Email", type: "email", showInTable: true },
  {
    name: "item_category",
    label: "Item Category",
    required: true,
    showInTable: true,
    lookup: { table: "item_categories", labelField: "name" },
  },
  {
    name: "item_name",
    label: "Item Name",
    required: true,
    showInTable: true,
  },
  {
    name: "quantity",
    label: "Quantity",
    type: "number",
    required: true,
    showInTable: true,
  },
  {
    name: "pickup_address",
    label: "Pickup Address",
    type: "textarea",
    required: true,
    hideInTable: true,
  },
  { name: "area", label: "Area", required: true, showInTable: true },
  { name: "landmark", label: "Landmark", showInTable: true },
  {
    name: "pincode",
    label: "Pincode",
    required: true,
    showInTable: true,
  },
  {
    name: "pickup_date",
    label: "Pickup Date",
    type: "date",
    readOnly: true,
    showInTable: true,
  },
  {
    name: "pickup_note",
    label: "Pickup Note",
    type: "textarea",
    readOnly: true,
    showInTable: true,
  },
  {
    name: "message",
    label: "Message",
    type: "textarea",
    hideInTable: true,
  },
  {
    name: "photo",
    label: "Photo URL",
    type: "url",
    readOnly: true,
    hideInTable: true,
  },
  {
    name: "remarks",
    label: "Remarks",
    type: "textarea",
    readOnly: true,
    hideInTable: true,
  },
  {
    name: "status",
    label: "Status",
    type: "select",
    showInTable: true,
    options: statusOptions,
  },
];

function toPayload(payload: Record<string, unknown>): ScrapDonationPayload {
  return {
    full_name: String(payload.full_name ?? "").trim(),
    mobile_number: String(payload.mobile_number ?? "").trim(),
    email: payload.email ? String(payload.email).trim() : undefined,
    item_category: Number(payload.item_category),
    item_name: String(payload.item_name ?? "").trim(),
    quantity: Number(payload.quantity),
    item_condition: "scrap",
    pickup_address: String(payload.pickup_address ?? "").trim(),
    area: String(payload.area ?? "").trim(),
    landmark: payload.landmark ? String(payload.landmark).trim() : undefined,
    pincode: String(payload.pincode ?? "").trim(),
    message: payload.message ? String(payload.message).trim() : undefined,
    status: payload.status
      ? (String(payload.status) as DonationRequestStatus)
      : undefined,
  };
}

function mapRecordToForm(
  record: ScrapDonationRecord
): Partial<ScrapDonationRecord> {
  return {
    ...record,
    item_category: record.item_category,
    item_category_name: record.item_category_name,
  };
}

export default function Page() {
  const [viewTarget, setViewTarget] = useState<ScrapDonationRecord | null>(
    null
  );

  return (
    <AdminShell
      title="Scrap Donation Requests"
      subtitle="Manage public scrap donation submissions with pickup details"
    >
      <ApiResourceTable<ScrapDonationRecord>
        title="Scrap Request"
        labelField="full_name"
        fields={fields}
        searchFields={[
          "full_name",
          "mobile_number",
          "email",
          "item_name",
          "area",
        ]}
        defaultValues={{ status: "new", quantity: 1 }}
        enableCsvExport
        csvFileName="scrap-donation-requests"
        statusFilterOptions={statusFilterOptions}
        lookupLoaders={{
          item_category: () => itemCategoryService.listLookupByType("scrap"),
        }}
        mapRecordToForm={mapRecordToForm}
        list={query => adminScrapDonationService.listPage(query)}
        create={payload => adminScrapDonationService.create(toPayload(payload))}
        update={(id, payload) =>
          adminScrapDonationService.update(id, toPayload(payload))
        }
        remove={id => adminScrapDonationService.delete(id)}
        onView={row => setViewTarget(row)}
        renderRowActions={(row, { refresh }) => (
          <DonationRequestStatusActions
            row={row}
            label={row.full_name}
            onUpdateStatus={(id, status, remarks) =>
              adminScrapDonationService.updateStatus(id, { status, remarks })
            }
            onComplete={refresh}
          />
        )}
        extraColumns={[
          {
            id: "pickup_mail",
            label: "Send Pickup Mail",
            cellClassName: "text-center",
            render: (row, { refresh }) => (
              <div className="flex justify-center">
                <DonationPickupUpdateAction
                  row={row}
                  label={row.full_name}
                  onSendPickupUpdate={(id, payload) =>
                    adminScrapDonationService.sendPickupUpdate(id, payload)
                  }
                  onComplete={refresh}
                />
              </div>
            ),
          },
        ]}
      />

      <DonationRequestDetailDialog
        open={!!viewTarget}
        onOpenChange={open => {
          if (!open) {
            setViewTarget(null);
          }
        }}
        requestId={viewTarget?.id ?? null}
        variant="scrap"
        preview={viewTarget}
      />
    </AdminShell>
  );
}
